import { Refine, Authenticated } from '@refinedev/core'
import { ThemedLayout, useNotificationProvider } from '@refinedev/antd'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ConfigProvider, App as AntdApp } from 'antd'
import srRS from 'antd/locale/sr_RS'
import routerProvider, { NavigateToResource } from '@refinedev/react-router'
import { Routes, Route, Outlet } from 'react-router-dom'
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  ExperimentOutlined,
  CalendarOutlined,
  VideoCameraOutlined,
  ShareAltOutlined,
  ShoppingOutlined,
  TagsOutlined,
  ShoppingCartOutlined,
  ReadOutlined,
} from '@ant-design/icons'
import { useState } from 'react'

import { dataProvider } from './providers/data-provider'
import { authProvider } from './providers/auth-provider'
import { accessControlProvider } from './providers/access-control'

import LoginPage from './pages/login'
import AuthCallbackPage from './pages/auth/callback'
import DashboardPage from './pages/dashboard'
import { Header } from './components/Header'
import { CustomSider } from './components/CustomSider'
import { PatientsListPage, PatientShowPage, PatientEditPage, PatientCreatePage } from './pages/patients'
import { DoctorsListPage, DoctorInvitePage } from './pages/doctors'
import { LabResultsListPage, LabResultShowPage } from './pages/lab-results'
import { ConsultationsListPage, ConsultationShowPage } from './pages/consultations'
import CalendarPage from './pages/calendar'
import MyConsultationsListPage from './pages/my-consultations/index'
import MyConsultationShowPage from './pages/my-consultations/show'
import DoctorCallPage from './pages/my-consultations/call'
import { SocialMediaListPage, SocialMediaCreatePage, SocialMediaShowPage, SocialMediaAccountsPage, SocialMediaCallbackPage, SocialMediaCalendarPage } from './pages/social-media'
import { ProductsListPage, ProductCreatePage, ProductEditPage, ProductShowPage } from './pages/products'
import { CategoriesListPage, CategoryCreatePage, CategoryEditPage } from './pages/categories'
import { OrdersListPage, OrderShowPage } from './pages/orders'
import { BlogListPage, BlogFormPage, BlogShowPage } from './pages/blog'

import '@refinedev/antd/dist/reset.css'

// Create QueryClient
const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
      },
    },
  })

// Resource definitions for Refine
const resources = [
  {
    name: 'dashboard',
    list: '/',
    meta: {
      label: 'Dashboard',
      icon: <DashboardOutlined />,
    },
  },
  {
    name: 'patients',
    list: '/patients',
    show: '/patients/:id',
    create: '/patients/create',
    edit: '/patients/:id/edit',
    meta: {
      label: 'Pacijenti',
      icon: <UserOutlined />,
    },
  },
  {
    name: 'doctors',
    list: '/doctors',
    show: '/doctors/:id',
    create: '/doctors/invite',
    edit: '/doctors/:id/edit',
    meta: {
      label: 'Lekari',
      icon: <TeamOutlined />,
    },
  },
  {
    name: 'lab-results',
    list: '/lab-results',
    show: '/lab-results/:id',
    create: '/lab-results/create',
    edit: '/lab-results/:id/edit',
    meta: {
      label: 'Lab rezultati',
      icon: <ExperimentOutlined />,
    },
  },
  {
    name: 'consultations',
    list: '/consultations',
    show: '/consultations/:id',
    meta: {
      label: 'Konsultacije',
      icon: <CalendarOutlined />,
    },
  },
  {
    name: 'calendar',
    list: '/calendar',
    meta: {
      label: 'Kalendar',
      icon: <CalendarOutlined />,
    },
  },
  {
    name: 'my-consultations',
    list: '/my-consultations',
    show: '/my-consultations/:id',
    meta: {
      label: 'Moje konsultacije',
      icon: <VideoCameraOutlined />,
    },
  },
  {
    name: 'social-media',
    list: '/social-media',
    show: '/social-media/:id',
    create: '/social-media/create',
    edit: '/social-media/:id/edit',
    meta: {
      label: 'Drustvene mreze',
      icon: <ShareAltOutlined />,
    },
  },
  {
    name: 'products',
    list: '/products',
    show: '/products/:id',
    create: '/products/create',
    edit: '/products/:id/edit',
    meta: {
      label: 'Proizvodi',
      icon: <ShoppingOutlined />,
    },
  },
  {
    name: 'categories',
    list: '/categories',
    create: '/categories/create',
    edit: '/categories/:id/edit',
    meta: {
      label: 'Kategorije',
      icon: <TagsOutlined />,
    },
  },
  {
    name: 'orders',
    list: '/orders',
    show: '/orders/:id',
    meta: {
      label: 'Porudžbine',
      icon: <ShoppingCartOutlined />,
    },
  },
  {
    name: 'blog',
    list: '/blog',
    show: '/blog/:id',
    create: '/blog/create',
    edit: '/blog/:id/edit',
    meta: {
      label: 'Blog',
      icon: <ReadOutlined />,
    },
  },
]

// Theme configuration - Enhanced for better UX
const theme = {
  token: {
    colorPrimary: '#1e3a5f', // Navy - primary brand color
    colorSuccess: '#0d9488', // Teal - success/health indicators
    colorWarning: '#f97316', // Coral - warnings, CTAs
    colorError: '#ef4444', // Error red
    colorInfo: '#0d9488', // Teal for info
    borderRadius: 8, // Increased from 6 for softer feel
    fontFamily: "'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif",
    fontSize: 14, // Ant Design default
    // Enhanced spacing
    padding: 16,
    margin: 16,
    lineHeight: 1.6, // Better readability
  },
  components: {
    Layout: {
      siderBg: '#1e3a5f', // Navy sidebar
      headerBg: '#ffffff',
      triggerBg: '#1e3a5f', // Collapse trigger background
      triggerColor: '#ffffff',
      // Content padding
      bodyBg: '#f5f5f5', // Light gray background for main content
    },
    Menu: {
      // Dark theme for sidebar menu
      darkItemBg: '#1e3a5f',
      darkSubMenuItemBg: '#1e3a5f',
      darkItemSelectedBg: 'rgba(255, 255, 255, 0.1)',
      darkItemHoverBg: 'rgba(255, 255, 255, 0.15)',
      darkItemColor: 'rgba(255, 255, 255, 0.85)',
      darkItemSelectedColor: '#ffffff',
      // Light theme fallback
      itemBg: 'transparent',
      itemSelectedBg: 'rgba(30, 58, 95, 0.1)',
      itemHoverBg: 'rgba(30, 58, 95, 0.05)',
      itemSelectedColor: '#1e3a5f',
    },
    Button: {
      colorPrimaryHover: '#2d4a6f', // Navy light
      // Enhanced button sizes for better touch targets
      paddingContentHorizontal: 24,
      paddingContentVertical: 8,
      // Large button enhancements
      paddingContentHorizontalLG: 32,
      paddingContentVerticalLG: 12,
      fontSizeLG: 16,
      // Better visual weight
      fontWeight: 500,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
      primaryShadow: '0 2px 8px rgba(30, 58, 95, 0.15)',
    },
    Card: {
      // Better card elevation
      boxShadowTertiary: '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
      paddingLG: 24, // More generous padding
      borderRadiusLG: 12, // Softer corners for cards
    },
    Typography: {
      // Enhanced heading hierarchy
      fontSizeHeading1: 38,
      fontSizeHeading2: 30,
      fontSizeHeading3: 24,
      fontSizeHeading4: 20,
      fontSizeHeading5: 16,
      // Better line heights for readability
      lineHeightHeading1: 1.2,
      lineHeightHeading2: 1.3,
      lineHeightHeading3: 1.4,
    },
    Table: {
      // Better table spacing
      cellPaddingBlock: 16,
      cellPaddingInline: 16,
      headerBg: '#fafafa',
      borderColor: '#e5e7eb',
    },
    Tag: {
      // Better tag spacing and readability
      fontSizeSM: 13,
      lineHeightSM: 1.4,
      borderRadiusSM: 6,
    },
    Alert: {
      // Enhanced alert styling
      paddingContentVerticalLG: 16,
      paddingContentHorizontalLG: 24,
    },
    List: {
      // Better list item spacing
      itemPadding: '16px 24px',
    },
  },
}

function App() {
  const [queryClient] = useState(createQueryClient)

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider locale={srRS} theme={theme}>
        <AntdApp>
            <Refine
              dataProvider={dataProvider}
              authProvider={authProvider}
              accessControlProvider={accessControlProvider}
              routerProvider={routerProvider}
              notificationProvider={useNotificationProvider()}
              resources={resources}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                projectId: 'perfectamed-admin',
              }}
            >
              <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />

              {/* Authenticated routes */}
              <Route
                element={
                  <Authenticated
                    key="authenticated-routes"
                    fallback={<div>Učitavanje...</div>}
                    redirectOnFail="/login"
                  >
                    <ThemedLayout
                      Header={() => <Header />}
                      Sider={() => (
                        <CustomSider
                          Title={({ collapsed }) => (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 8,
                              color: '#1e3a5f',
                              padding: '16px 8px',
                              width: '100%',
                              minHeight: 64,
                            }}>
                              {!collapsed && <span style={{ fontWeight: 600, fontSize: 18 }}>PerfectaMed</span>}
                              {collapsed && <span style={{ fontWeight: 600, fontSize: 14 }}>PM</span>}
                            </div>
                          )}
                        />
                      )}
                    >
                      <Outlet />
                    </ThemedLayout>
                  </Authenticated>
                }
              >
                <Route index element={<DashboardPage />} />

                {/* Patients */}
                <Route path="/patients">
                  <Route index element={<PatientsListPage />} />
                  <Route path="create" element={<PatientCreatePage />} />
                  <Route path=":id" element={<PatientShowPage />} />
                  <Route path=":id/edit" element={<PatientEditPage />} />
                </Route>

                {/* Doctors */}
                <Route path="/doctors">
                  <Route index element={<DoctorsListPage />} />
                  <Route path="invite" element={<DoctorInvitePage />} />
                </Route>

                {/* Lab Results */}
                <Route path="/lab-results">
                  <Route index element={<LabResultsListPage />} />
                  <Route path=":id" element={<LabResultShowPage />} />
                </Route>

                {/* Consultations */}
                <Route path="/consultations">
                  <Route index element={<ConsultationsListPage />} />
                  <Route path=":id" element={<ConsultationShowPage />} />
                </Route>

                {/* Calendar View */}
                <Route path="/calendar" element={<CalendarPage />} />

                {/* My Consultations (Doctor) */}
                <Route path="/my-consultations">
                  <Route index element={<MyConsultationsListPage />} />
                  <Route path=":id" element={<MyConsultationShowPage />} />
                  <Route path=":id/call" element={<DoctorCallPage />} />
                </Route>

                {/* Social Media */}
                <Route path="/social-media">
                  <Route index element={<SocialMediaListPage />} />
                  <Route path="create" element={<SocialMediaCreatePage />} />
                  <Route path="calendar" element={<SocialMediaCalendarPage />} />
                  <Route path="accounts" element={<SocialMediaAccountsPage />} />
                  <Route path="accounts/callback" element={<SocialMediaCallbackPage />} />
                  <Route path=":id" element={<SocialMediaShowPage />} />
                  <Route path=":id/edit" element={<SocialMediaCreatePage />} />
                </Route>

                {/* Products */}
                <Route path="/products">
                  <Route index element={<ProductsListPage />} />
                  <Route path="create" element={<ProductCreatePage />} />
                  <Route path=":id" element={<ProductShowPage />} />
                  <Route path=":id/edit" element={<ProductEditPage />} />
                </Route>

                {/* Categories */}
                <Route path="/categories">
                  <Route index element={<CategoriesListPage />} />
                  <Route path="create" element={<CategoryCreatePage />} />
                  <Route path=":id/edit" element={<CategoryEditPage />} />
                </Route>

                {/* Orders */}
                <Route path="/orders">
                  <Route index element={<OrdersListPage />} />
                  <Route path=":id" element={<OrderShowPage />} />
                </Route>

                {/* Blog */}
                <Route path="/blog">
                  <Route index element={<BlogListPage />} />
                  <Route path="create" element={<BlogFormPage />} />
                  <Route path=":id" element={<BlogShowPage />} />
                  <Route path=":id/edit" element={<BlogFormPage />} />
                </Route>

                {/* Catch all */}
                <Route path="*" element={<NavigateToResource resource="dashboard" />} />
              </Route>
              </Routes>
            </Refine>
        </AntdApp>
      </ConfigProvider>
    </QueryClientProvider>
  )
}

export default App
